import { Controller } from "@hotwired/stimulus"

// CSS-only bar chart for displaying revenue trends
export default class extends Controller {
  static values = {
    data: Array,
    period: Number
  }

  connect() {
    this.barListeners = []
    this.boundShowTooltip = this.showTooltip.bind(this)
    this.boundHideTooltip = this.hideTooltip.bind(this)
    this.render()
  }

  render() {
    // Clean up any existing listeners before re-rendering
    this.cleanupBarListeners()

    const data = this.dataValue || []
    if (data.length === 0) {
      this.element.innerHTML = this.emptyState()
      return
    }

    const maxRevenue = Math.max(...data.map(d => d.revenue_cents))
    const showLabels = this.periodValue <= 14

    this.element.innerHTML = `
      <div class="flex items-end justify-between h-full gap-1 pb-6 relative">
        ${data.map((d, i) => this.renderBar(d, maxRevenue, showLabels, i, data.length)).join('')}
      </div>
    `

    // Add hover event listeners and track them for cleanup
    this.element.querySelectorAll('[data-tooltip]').forEach(bar => {
      bar.addEventListener('mouseenter', this.boundShowTooltip)
      bar.addEventListener('mouseleave', this.boundHideTooltip)
      this.barListeners.push(bar)
    })
  }

  cleanupBarListeners() {
    if (this.barListeners) {
      this.barListeners.forEach(bar => {
        bar.removeEventListener('mouseenter', this.boundShowTooltip)
        bar.removeEventListener('mouseleave', this.boundHideTooltip)
      })
      this.barListeners = []
    }
  }

  renderBar(data, maxRevenue, showLabels, index, total) {
    const heightPercent = maxRevenue > 0 ? (data.revenue_cents / maxRevenue) * 100 : 0
    const minHeight = heightPercent > 0 ? Math.max(heightPercent, 4) : 2
    const formattedDate = this.escapeHtml(this.formatDate(data.date))
    const formattedRevenue = this.escapeHtml(this.formatCurrency(data.revenue_cents))
    const orderCount = parseInt(data.order_count, 10) || 0
    const barWidth = total <= 7 ? 'flex-1 max-w-16' : 'flex-1'
    const dateLabel = this.escapeHtml(this.shortDate(data.date))

    return `
      <div class="${barWidth} flex flex-col items-center">
        <div class="w-full flex flex-col items-center justify-end" style="height: calc(100% - ${showLabels ? '24px' : '0px'})">
          <div data-tooltip="${formattedDate}: ${formattedRevenue} (${orderCount} order${orderCount === 1 ? '' : 's'})"
               class="w-full bg-emerald-500 hover:bg-emerald-600 rounded-t cursor-pointer transition-colors relative"
               style="height: ${minHeight}%">
          </div>
        </div>
        ${showLabels ? `<div class="text-xs text-slate-500 mt-1 whitespace-nowrap">${dateLabel}</div>` : ''}
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  showTooltip(event) {
    const bar = event.currentTarget
    const text = bar.dataset.tooltip

    // Remove any existing tooltip
    this.hideTooltip()

    const tooltip = document.createElement('div')
    tooltip.id = 'sparkline-tooltip'
    tooltip.className = 'absolute z-50 bg-slate-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap'
    tooltip.textContent = text

    // Position above the bar
    const rect = bar.getBoundingClientRect()

    tooltip.style.position = 'fixed'
    tooltip.style.left = `${rect.left + rect.width / 2}px`
    tooltip.style.top = `${rect.top - 8}px`
    tooltip.style.transform = 'translate(-50%, -100%)'

    document.body.appendChild(tooltip)
  }

  hideTooltip() {
    const existing = document.getElementById('sparkline-tooltip')
    if (existing) {
      existing.remove()
    }
  }

  formatDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  shortDate(dateStr) {
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  formatCurrency(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100)
  }

  emptyState() {
    return `
      <div class="h-full flex items-center justify-center text-slate-500">
        <div class="text-center">
          <div class="text-4xl mb-2">&#x1F4C8;</div>
          <p>No data available</p>
        </div>
      </div>
    `
  }

  disconnect() {
    this.cleanupBarListeners()
    this.hideTooltip()
  }
}
